import clsx from 'clsx';
import { Column } from 'react-table';
import Header from '../../../../../../modules/components/datatable/Header';
import { BrandImage } from './BrandDetectionDetail';
import SimpleDatatable from '../../../../../../modules/components/datatable/SimpleDatatable';
import UnlabeledBulletColumns from '../../../../../../modules/components/list/UnlabeledBulletColumns';
import { useState } from 'react';
import { toTitleCase } from '../../../../../../../_metronic/helpers';
import ToggleSwitch from '../../../../../../modules/components/toggleSwitch/ToggleSwitch';
import { useIntl } from 'react-intl';

interface Props {
  data: Array<{
    name: string;
    sector?: string;
    snippets?: string[];
    images?: BrandImage[];
  }>;
  hiddenBrands: Set<string>;
  onToggleVisibility: (brand: string, sector?: string) => void;
  onExpandImages: (brand: string, images: BrandImage[]) => void;
}

export default function BrandTable({
  data,
  hiddenBrands,
  onToggleVisibility,
  onExpandImages,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_BRAND_DETAIL.${id}` }, values);
  const [expandedSnippets, setExpandedSnippets] = useState<Set<string>>(new Set());
  const initialSnippetsNum = 3;
  const emDash = '—';

  const columns: Column<Props['data'][number]>[] = [
    {
      Header: () => <Header>{t('BRAND')}</Header>,
      accessor: (row) => (
        <div className={clsx('d-block', hiddenBrands?.has(row.name) && 'opacity-50')}>
          <ToggleSwitch
            isVisible={!hiddenBrands?.has(row.name)}
            onToggle={() => {
              onToggleVisibility(row.name, row?.sector);
            }}
            size='sm'
            className={{ container: 'd-inline-block me-2', switch: 'align-middle' }}
          />
          <span
            className={clsx(
              'text-wrap',
              hiddenBrands?.has(row.name) && 'text-decoration-line-through'
            )}
            style={{ maxWidth: '100px' }}
          >
            {row.name}
          </span>
          {/* {row?.sector && (
            <span className='badge badge-light badge-sm ms-2 align-middle'>
              {toTitleCase(row.sector?.replaceAll('_', ' '))}
            </span>
          )} */}
        </div>
      ),
      minWidth: 80,
      id: 'name',
    },
    {
      Header: () => <Header>{t('SOURCE')}</Header>,
      accessor: (row) => {
        const showAllSnippets = row?.snippets?.length > 0 && expandedSnippets.has(row.name);
        const snippets = showAllSnippets
          ? row?.snippets
          : row?.snippets?.slice(0, initialSnippetsNum);

        return (
          <div className={clsx(hiddenBrands?.has(row.name) && 'opacity-50')}>
            {row?.images?.length > 0 && (
              <button
                type='button'
                className='btn btn-sm btn-light-primary fw-bold badge py-2 px-3'
                onClick={() => {
                  onExpandImages(row.name, row.images);
                }}
              >
                <i className='fas fa-external-link-alt me-1'></i>
                {t('VIEW_IMAGE_COUNT', { count: row.images.length })}
              </button>
            )}
            {row?.snippets?.length > 0 ? (
              <>
                <UnlabeledBulletColumns
                  columnCount={1}
                  data={snippets.map((snippet, sIndex) => {
                    const regex = new RegExp(`(${row.name})`, 'gi');
                    const parts = snippet.split(regex);

                    return (
                      <div key={sIndex}>
                        {parts.map((part, pIndex) =>
                          regex.test(part) ? (
                            <span key={pIndex} className='mark align-middle'>
                              {part}
                            </span>
                          ) : (
                            <span key={pIndex}>{part}</span>
                          )
                        )}
                      </div>
                    );
                  })}
                  shouldBreakText
                />
                {row.snippets?.length > initialSnippetsNum && (
                  <button
                    className='btn btn-link btn-color-muted btn-active-color-primary btn-sm py-1'
                    onClick={(e) => {
                      e.currentTarget.blur();
                      setExpandedSnippets((prev) => {
                        const newSet = new Set(prev);
                        if (showAllSnippets) newSet.delete(row.name);
                        else newSet.add(row.name);
                        return newSet;
                      });
                    }}
                  >
                    {showAllSnippets ? (
                      <>
                        {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                      </>
                    ) : (
                      <>
                        {t('SHOW_ALL_MORE', {
                          remaining: row.snippets?.length - initialSnippetsNum,
                        })}
                        <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                      </>
                    )}
                  </button>
                )}
              </>
            ) : (
              emDash
            )}
          </div>
        );
      },
      minWidth: 200,
      id: 'source',
    },
  ];

  return <SimpleDatatable data={data} columns={columns} className='m-0' />;
}
